import React, { useState, useEffect, useMemo, useRef } from "react";
import i18n from "i18next";
import { connect, useDispatch } from "react-redux";
import CanvasWorkflowPanel from "../../canvas_workflow/CanvasWorkflowPanel";
import ShowCodePanel from "../../workflow/ShowCodePanel";
// === Pedagogy Reviewer ===
import ReviewDrawer from "../../workflow/ReviewDrawer";
import { runPedagogyReview } from "../../../services/workflowReviewService";
// === /Pedagogy Reviewer ===
import UnreachableBadge from "../../workflow/UnreachableBadge";
import ScoringModeToggle from "../../workflow/ScoringModeToggle";
import ActionPicker from "../../workflow/ActionPicker";
import { WorkflowEditContext } from "../../workflow/WorkflowEditContext";
import { selectSectionWorkflow } from "../../workflow/selectors/selectSectionWorkflow";
import {
  addWorkflowTrigger,
  updateWorkflowTrigger,
  updateItemParam,
  translateSceneThunk,
} from "../../../../common/actions";
// === Translate Scene ===
import TranslatePicker from "../../workflow/TranslatePicker";
// === /Translate Scene ===
import PresetsSubstep from "./WorkflowTab/PresetsSubstep";
import WizardSubstep from "./WorkflowTab/WizardSubstep";
import "./WorkflowTab.scss";

// Sensible default shape per action kind, used by the +Add ActionPicker bridge.
// Keep these minimal — downstream editors fill in details. Shapes match
// ActionNode.summarize() and the action types consumed by the Visor runtime.
//
// When kind === "showInteraction" the picker passes an extras bag with
// { itemKind, params }. The action INLINES that interaction definition (no
// separate InteractivePlugin item is created): buildGraph reads
// `action.interactionKind` to emit specialised nodes (branchingQuestionNode /
// endScreenNode); the Visor reads the same fields at runtime.
function makeDefaultAction(kind, extras) {
  switch (kind) {
    case "resume":
      return { kind: "resume" };
    case "showText":
      return { kind: "showText", text: { en: "" } };
    case "showImage":
      return { kind: "showImage", imageId: "" };
    case "playVideo":
      return { kind: "playVideo", assetId: "" };
    case "setVar":
      return { kind: "setVar", varId: "", op: "set", value: "" };
    case "jumpScene":
      return { kind: "jumpScene", target: "" };
    case "replayItem":
      return { kind: "replayItem", pluginBoxId: "" };
    case "wait":
      return { kind: "wait", ms: 1000 };
    case "branch":
      return { kind: "branch", branches: [] };
    case "showBox":
      return { kind: "showBox", targetBoxId: "" };
    case "hideBox":
      return { kind: "hideBox", targetBoxId: "" };
    case "showInteraction": {
      const interactionKind = extras && extras.itemKind;
      const params = (extras && extras.params) || {};
      if (!interactionKind) return { kind: "showInteraction" };
      return { kind: "showInteraction", interactionKind, params };
    }
    default:
      return { kind: kind || "resume" };
  }
}

// Event-name options offered by the inline +Trigger form. The two
// outcome-based options use the namespaced "onAnswer.*" form (matches what
// buildElementGraph / generateBaselineWorkflow produce after Round 23 review
// #3); scene-level events (onShow, onMarkClick, onSelect, …) stay as bare
// names since they do not have a correct/wrong outcome to namespace.
const TRIGGER_EVENT_OPTIONS = [
  "onShow",
  "onAnswer.correct",
  "onAnswer.wrong",
  "onSelect",
  "onFlip",
  "onSubmit",
  "custom",
];

// Short random id (lowercase alphanumerics). Kept inline — there is no shared
// id helper in this tree and pulling in `nanoid` for a 6-char id is overkill.
function random6() {
  return Math.random().toString(36).slice(2, 8);
}

// Default InteractivePlugin item params per itemKind. Kept aligned with the
// item renderers in plugins/InteractivePlugin/components/itemKinds/*. The shape
// must be enough for the Visor to render the item; the author refines from there.
function defaultParamsFor(kind) {
  switch (kind) {
    case "FlipCard":
      return { front: "Front", back: "Back" };
    case "YesNo":
      return { prompt: "Yes or no?", correctAnswer: true };
    case "TrueFalse":
      return { prompt: "True or false?", correctAnswer: true };
    case "MCQ":
    case "MultipleChoice":
      return {
        prompt: "Pick one",
        options: [
          { key: "a", label: "A" },
          { key: "b", label: "B" },
        ],
        correctAnswer: "a",
      };
    case "MultipleAnswer":
      return {
        prompt: "Pick all",
        options: [
          { key: "a", label: "A" },
          { key: "b", label: "B" },
        ],
        correctAnswers: ["a"],
      };
    case "FillInBlank":
      return { prompt: "Type the answer", acceptedAnswers: [""] };
    case "DragDrop":
      return {
        prompt: "Drag",
        items: [{ key: "a", label: "A" }],
        targets: [{ key: "x", label: "X" }],
        correctPlacements: { a: "x" },
      };
    case "Matching":
      return {
        prompt: "Match",
        leftItems: [{ key: "a", label: "A" }],
        rightItems: [{ key: "x", label: "X" }],
        correctPairs: { a: "x" },
      };
    case "Ordering":
      return {
        prompt: "Order",
        items: [
          { key: "a", label: "A" },
          { key: "b", label: "B" },
        ],
        correctOrder: ["a", "b"],
      };
    default:
      return {};
  }
}

function WorkflowTab({
  sectionId,
  triggers,
  items,
  scenes,
  interactiveBoxes,
  variables,
  scoringMode,
  unreachableNodeIds,
  selectedPluginBoxId,
  currentSceneId,
}) {
  const [substep, setSubstep] = useState("graph");

  // === Add-Interaction ActionPicker bridge ===
  // The primary "+ Add Interaction" toolbar button opens the new ActionPicker
  // portal directly (Interactions group on top, then plain actions), instead
  // of the legacy 5-category WizardSubstep. The wizard remains reachable via
  // a de-emphasised "Use wizard instead" link below the button.
  const addInteractionBtnRef = useRef(null);
  const [addPickerOpen, setAddPickerOpen] = useState(false);

  // Unreachable-node badge: shows a chip count; clicking it used to pulse the
  // legacy GraphCanvas nodes. With the modal body now rendering
  // CanvasWorkflowPanel (which owns its own selection/highlight via Redux),
  // the pulse is a no-op until the modal-mode panel exposes a highlight prop.
  const unreachable = Array.isArray(unreachableNodeIds) ? unreachableNodeIds : [];
  const handlePulseUnreachable = () => {};

  // Transparency: surfaces the raw workflow JSON in a read-only modal so
  // developers / curious authors can inspect the source-of-truth shape that
  // the node graph compiles to. No edits — view + copy only.
  const [showCode, setShowCode] = useState(false);

  // === Pedagogy Reviewer ===
  // Slide-in drawer state for the LLM-generated pedagogy review. Sits in the
  // same graph-container scope as ShowCodePanel (no popup over a popup).
  const [showReview, setShowReview] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);

  const handleRunReview = async () => {
    // Inline guard mirrors the disabled button: nothing to review without a
    // current section. Rename the response var to avoid shadowing the
    // component prop `items` which we also pass into workflowJson.
    if (sectionId === null || sectionId === undefined) return;
    setShowReview(true);
    setReviewLoading(true);
    setReviewError(null);
    try {
      const result = await runPedagogyReview(sectionId, {
        variables,
        triggers,
        items,
        scoringMode,
      });
      setReviewItems(Array.isArray(result) ? result : []);
    } catch (err) {
      setReviewError(err && err.message ? err.message : String(err));
      setReviewItems([]);
    } finally {
      setReviewLoading(false);
    }
  };
  // === /Pedagogy Reviewer ===

  // === Translate Scene ===
  // Locale-picker popover state for the "Translate scene" toolbar action.
  // Open state is local; busy state is set while the thunk awaits the server.
  // The picker is rendered inline below the toolbar button (no popup over
  // popup); selection triggers translateSceneThunk for the current scene.
  const translateBtnRef = useRef(null);
  const [translatePickerOpen, setTranslatePickerOpen] = useState(false);
  const [translateBusy, setTranslateBusy] = useState(false);

  const handleTranslatePick = async (locale) => {
    if (!hasSection || !currentSceneId) {
      setTranslatePickerOpen(false);
      return;
    }
    setTranslateBusy(true);
    try {
      await dispatch(
        translateSceneThunk({
          sectionId,
          sceneId: currentSceneId,
          targetLocale: locale,
        })
      );
    } finally {
      setTranslateBusy(false);
      setTranslatePickerOpen(false);
    }
  };
  // === /Translate Scene ===

  // Inline +Trigger form state. `formOpen` toggles the floating panel; the
  // three field states hold the in-progress selection. eventName defaults to
  // `onShow` (the most common case); pluginBoxId defaults to "*" (any box).
  const [formOpen, setFormOpen] = useState(false);
  const [formEventName, setFormEventName] = useState("onShow");
  const [formSceneId, setFormSceneId] = useState("");
  const [formPluginBoxId, setFormPluginBoxId] = useState("*");

  // === Phase 5.3 === Replaced v1 window-bridges (window.__appendActionToTrigger
  // and window.__replaceActionAt) with a React context-based hook so multiple
  // editor mounts don't clobber each other's closures. The window-bridges are
  // still installed below as a fallback for any in-transition callers; once all
  // call sites use useWorkflowEdit(), the window installs can be removed.
  const dispatch = useDispatch();
  const editApi = useMemo(
    () => ({
      appendActionToTrigger: (triggerId, kind, extras) => {
        if (!triggerId || sectionId === null || sectionId === undefined) return;
        const trigger = triggers.find((t) => t.id === triggerId);
        if (!trigger) return;
        // Preserve the picker's extras (itemKind + params for showInteraction)
        // so the appended action carries the inline interaction definition.
        const defaultAction = makeDefaultAction(kind || "resume", extras);
        const nextActions = [
          ...(Array.isArray(trigger.actions) ? trigger.actions : []),
          defaultAction,
        ];
        dispatch(updateWorkflowTrigger(sectionId, triggerId, { actions: nextActions }));
      },
      replaceActionAt: (triggerId, index, nextAction) => {
        if (!triggerId || sectionId === null || sectionId === undefined) return;
        const trigger = triggers.find((t) => t.id === triggerId);
        if (!trigger) return;
        const acts = Array.isArray(trigger.actions) ? trigger.actions.slice() : [];
        if (index < 0 || index >= acts.length) return;
        acts[index] = nextAction;
        dispatch(updateWorkflowTrigger(sectionId, triggerId, { actions: acts }));
      },
      // BranchingQuestion per-alternative chip wire-up — INLINE shape.
      //
      // The BQ now lives as an inline showInteraction action carried on a
      // trigger: `triggers[].actions[i] = { kind: "showInteraction",
      // interactionKind: "BranchingQuestion", params: { alternatives: [
      //   { id, label, actions: [...], ... }
      // ] } }`. Each alternative owns its action chain *inline* under
      // `alternatives[i].actions[]` (option A from the design doc) — no
      // separate trigger materialisation, no item-side patch.
      //
      // Locator: caller passes `{ triggerId, actionIndex, alternativeId }`
      // (buildGraph already surfaces triggerId + index on the
      // branchingQuestionNode data). We mutate the matching showInteraction
      // action's params.alternatives[i].actions[] and dispatch one
      // UPDATE_WORKFLOW_TRIGGER with the rebuilt trigger.actions array.
      // Graded-item outcome actions — appends an action to the item's
      // onCorrectActions or onWrongActions array directly (not a trigger).
      // `outcome` is "correct" or "wrong"; `action` is a plain action object.
      appendOutcomeAction: (boxId, itemId, outcome, action) => {
        if (!boxId || !itemId || !outcome || !action) return;
        const item = (items || []).find((it) => it && it.id === itemId);
        const p = (item && item.params) || {};
        const field = outcome === "correct" ? "onCorrectActions" : "onWrongActions";
        const current = Array.isArray(p[field]) ? p[field] : [];
        dispatch(updateItemParam(boxId, itemId, ["params", field], [...current, action]));
      },
      appendClickAction: (boxId, itemId, action) => {
        if (!boxId || !itemId || !action) return;
        const item = (items || []).find((it) => it && it.id === itemId);
        const p = (item && item.params) || {};
        const current = Array.isArray(p.onClickActions) ? p.onClickActions : [];
        dispatch(
          updateItemParam(boxId, itemId, ["params", "onClickActions"], [...current, action])
        );
      },
      appendActionToAlternative: (locator, kind, extras) => {
        if (!locator || typeof locator !== "object") return;
        const { triggerId, actionIndex, alternativeId } = locator;
        if (!triggerId || !alternativeId) return;
        if (typeof actionIndex !== "number" || actionIndex < 0) return;
        if (sectionId === null || sectionId === undefined) return;

        const trigger = triggers.find((t) => t.id === triggerId);
        if (!trigger) return;
        const actions = Array.isArray(trigger.actions) ? trigger.actions : [];
        const targetAction = actions[actionIndex];
        if (
          !targetAction ||
          targetAction.kind !== "showInteraction" ||
          targetAction.interactionKind !== "BranchingQuestion"
        ) {
          return;
        }
        const params = (targetAction && targetAction.params) || {};
        const alternatives = Array.isArray(params.alternatives) ? params.alternatives : [];
        const altIdx = alternatives.findIndex((a) => a && a.id === alternativeId);
        if (altIdx < 0) return;

        const defaultAction = makeDefaultAction(kind || "resume", extras);
        const nextAlternatives = alternatives.slice();
        const alt = nextAlternatives[altIdx];
        const altActions = Array.isArray(alt && alt.actions) ? alt.actions : [];
        nextAlternatives[altIdx] = { ...alt, actions: [...altActions, defaultAction] };

        const nextActions = actions.slice();
        nextActions[actionIndex] = {
          ...targetAction,
          params: { ...params, alternatives: nextAlternatives },
        };

        dispatch(updateWorkflowTrigger(sectionId, triggerId, { actions: nextActions }));
      },
    }),
    [dispatch, sectionId, triggers, items]
  );

  // Back-compat window-bridges — point at the same memoised handlers so any
  // caller still using window.__appendActionToTrigger / __replaceActionAt
  // continues to work during the transition. Remove once all callers migrate.
  useEffect(() => {
    window.__appendActionToTrigger = editApi.appendActionToTrigger;
    window.__replaceActionAt = editApi.replaceActionAt;
    window.__appendActionToAlternative = editApi.appendActionToAlternative;
    window.__appendOutcomeAction = editApi.appendOutcomeAction;
    window.__appendClickAction = editApi.appendClickAction;
    return () => {
      if (window.__appendActionToTrigger === editApi.appendActionToTrigger) {
        delete window.__appendActionToTrigger;
      }
      if (window.__replaceActionAt === editApi.replaceActionAt) {
        delete window.__replaceActionAt;
      }
      if (window.__appendActionToAlternative === editApi.appendActionToAlternative) {
        delete window.__appendActionToAlternative;
      }
      if (window.__appendOutcomeAction === editApi.appendOutcomeAction) {
        delete window.__appendOutcomeAction;
      }
      if (window.__appendClickAction === editApi.appendClickAction) {
        delete window.__appendClickAction;
      }
    };
  }, [editApi]);
  // === /Phase 5.3 ===

  // === Phase 6.3 === Read-only bridge so ActionEditor instances rendered
  // outside the connected tree (e.g. ActionNode inline popovers) can resolve
  // the current section's declared variables without prop-drilling. Keyed by
  // sectionId so the bridge survives section changes.
  useEffect(() => {
    window.__getVariables = (queriedSectionId) => {
      if (sectionId === null || sectionId === undefined) return [];
      // eslint-disable-next-line eqeqeq
      if (queriedSectionId != null && queriedSectionId != sectionId) return [];
      return Array.isArray(variables) ? variables : [];
    };
    return () => {
      if (window.__getVariables) delete window.__getVariables;
    };
  }, [sectionId, variables]);
  // === /Phase 6.3 ===

  // Scene bridge — lets ActionEditor/QuickActionEditor instances outside the
  // connected tree (e.g. ActionNode inline popovers) resolve the scene list
  // without prop-drilling.
  useEffect(() => {
    window.__getScenes = () => (Array.isArray(scenes) ? scenes : []);
    return () => {
      if (window.__getScenes) delete window.__getScenes;
    };
  }, [scenes]);

  const hasSection = sectionId !== null && sectionId !== undefined;

  // Available scenes within the current section. We filter against `parent`
  // with `==` (string id vs numeric id) to match the selector's existing
  // convention. The first scene becomes the default selection when the form
  // opens.
  const sectionScenes = Array.isArray(scenes) ? scenes : [];
  // InteractivePlugin boxes restricted to the chosen scene (if any). Falls
  // back to all interactive boxes in the section when no scene picked.
  const filteredInteractiveBoxes = (interactiveBoxes || []).filter((b) => {
    if (!formSceneId) return true;
    return String(b.sceneId) === String(formSceneId);
  });

  const openForm = () => {
    setFormEventName("onShow");
    // Default to the first scene in the section (if any) — saves a click.
    setFormSceneId(sectionScenes[0] ? String(sectionScenes[0].id) : "");
    setFormPluginBoxId("*");
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  const submitForm = (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (!hasSection) return;
    const pluginBoxId = formPluginBoxId && formPluginBoxId !== "*" ? formPluginBoxId : null;
    const trigger = {
      id: `t_${random6()}`,
      sceneId: formSceneId || null,
      pluginBoxId,
      on: formEventName,
      actions: [],
    };
    dispatch(addWorkflowTrigger(sectionId, trigger));
    setFormOpen(false);
  };

  // Picker → "create-trigger-with-this-action" bridge. Behaviour:
  //   - If no triggers exist yet: dispatch addWorkflowTrigger with a default
  //     `{ on: "onShow", sceneId: currentSceneId, pluginBoxId: null }` shell
  //     whose actions start with the picked action.
  //   - If at least one trigger exists: append the picked action to the most
  //     recently created trigger (last entry in the triggers array, matching
  //     how addWorkflowTrigger reducers push to the tail).
  // `extras` carries `{ itemKind }` for the `showInteraction` rows;
  // makeDefaultAction unpacks that into the inline `interactionKind`/`params`
  // contract — we just forward the extras bag.
  const addInteractionFromPicker = (kind, extras) => {
    if (!hasSection) return;
    const action = makeDefaultAction(kind || "resume", extras);
    const triggerList = Array.isArray(triggers) ? triggers : [];
    if (triggerList.length === 0) {
      const trigger = {
        id: `t_${random6()}`,
        sceneId: currentSceneId || null,
        pluginBoxId: null,
        on: "onShow",
        actions: [action],
      };
      dispatch(addWorkflowTrigger(sectionId, trigger));
      return;
    }
    const target = triggerList[triggerList.length - 1];
    const nextActions = [...(Array.isArray(target.actions) ? target.actions : []), action];
    dispatch(updateWorkflowTrigger(sectionId, target.id, { actions: nextActions }));
  };

  return (
    <WorkflowEditContext.Provider value={editApi}>
      <div className="workflow-tab" data-testid={`workflow-tab__${substep}`}>
        <div className="workflow-tab__toolbar">
          {substep === "graph" && (
            <>
              <button
                type="button"
                className="workflow-tab__toolbar-btn workflow-tab__toolbar-btn--secondary"
                onClick={() => (formOpen ? closeForm() : openForm())}
                disabled={!hasSection}
                data-testid="workflow-tab__add-trigger-btn"
              >
                <i className="material-icons">add</i>
                {i18n.t("Workflow.add_trigger", { defaultValue: "+ Trigger" })}
              </button>
              <button
                type="button"
                className="workflow-tab__toolbar-btn"
                onClick={() => setSubstep("presets")}
              >
                <i className="material-icons">collections_bookmark</i>
                {i18n.t("Workflow.add_template", { defaultValue: "Use a template" })}
              </button>
              <div className="workflow-tab__add-interaction">
                <button
                  ref={addInteractionBtnRef}
                  type="button"
                  className="workflow-tab__toolbar-btn workflow-tab__toolbar-btn--primary"
                  onClick={() => setAddPickerOpen((open) => !open)}
                  disabled={!hasSection}
                  data-testid="workflow-tab__add-interaction-btn"
                  aria-haspopup="menu"
                  aria-expanded={addPickerOpen}
                >
                  <i className="material-icons">star</i>
                  {i18n.t("Workflow.add_interaction", { defaultValue: "Add Interaction" })}
                </button>
                <button
                  type="button"
                  className="workflow-tab__use-wizard-link"
                  onClick={() => {
                    setAddPickerOpen(false);
                    setSubstep("wizard");
                  }}
                  data-testid="workflow-tab__use-wizard-link"
                >
                  {i18n.t("Workflow.use_wizard_instead", {
                    defaultValue: "Use wizard instead",
                  })}
                </button>
              </div>
              {addPickerOpen && (
                <ActionPicker
                  anchorEl={addInteractionBtnRef.current}
                  onPick={(kind, extras) => addInteractionFromPicker(kind, extras)}
                  onClose={() => setAddPickerOpen(false)}
                />
              )}
              <button
                type="button"
                className="workflow-tab__toolbar-btn"
                onClick={() => setShowCode(true)}
                title="Show workflow JSON"
              >
                <i className="material-icons">code</i>
                Show Code
              </button>
              {/* === Pedagogy Reviewer === */}
              <button
                type="button"
                className="workflow-tab__toolbar-btn"
                onClick={handleRunReview}
                disabled={!hasSection || reviewLoading}
                title={i18n.t("Workflow.review_tooltip", {
                  defaultValue: "Run a pedagogical review of this workflow",
                })}
                data-testid="workflow-tab__review-btn"
              >
                <i className="material-icons">school</i>
                {i18n.t("Workflow.review_workflow", {
                  defaultValue: "Review workflow",
                })}
              </button>
              {/* === /Pedagogy Reviewer === */}
              {/* === Translate Scene === */}
              <div className="workflow-tab__translate">
                <button
                  ref={translateBtnRef}
                  type="button"
                  className="workflow-tab__toolbar-btn"
                  onClick={() => setTranslatePickerOpen((open) => !open)}
                  disabled={!hasSection || !currentSceneId || translateBusy}
                  title={i18n.t("Workflow.translate_tooltip", {
                    defaultValue: "Translate the current scene's interactions",
                  })}
                  data-testid="workflow-tab__translate-btn"
                  aria-haspopup="menu"
                  aria-expanded={translatePickerOpen}
                >
                  <i className="material-icons">translate</i>
                  {i18n.t("Workflow.translate_scene", {
                    defaultValue: "Translate scene",
                  })}
                </button>
                {translatePickerOpen && (
                  <TranslatePicker
                    disabled={!hasSection || !currentSceneId}
                    busy={translateBusy}
                    onPick={handleTranslatePick}
                    onClose={() => setTranslatePickerOpen(false)}
                  />
                )}
              </div>
              {/* === /Translate Scene === */}
              <ScoringModeToggle />
              <div className="workflow-tab__counter">
                <span>
                  {items.length} item{items.length === 1 ? "" : "s"}
                </span>
                <span>·</span>
                <span>
                  {triggers.length} trigger{triggers.length === 1 ? "" : "s"}
                </span>
                {sectionId === null || sectionId === undefined ? (
                  <span className="workflow-tab__counter-warn"> · no section</span>
                ) : null}
              </div>
              <UnreachableBadge count={unreachable.length} onClick={handlePulseUnreachable} />
              {formOpen && (
                <form
                  className="workflow-tab__add-trigger-form"
                  onSubmit={submitForm}
                  data-testid="workflow-tab__add-trigger-form"
                >
                  <label className="workflow-tab__add-trigger-field">
                    <span>{i18n.t("Workflow.event", { defaultValue: "Event" })}</span>
                    <select
                      value={formEventName}
                      onChange={(e) => setFormEventName(e.target.value)}
                    >
                      {TRIGGER_EVENT_OPTIONS.map((evt) => (
                        <option key={evt} value={evt}>
                          {evt}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="workflow-tab__add-trigger-field">
                    <span>{i18n.t("Workflow.scene", { defaultValue: "Scene" })}</span>
                    <select
                      value={formSceneId}
                      onChange={(e) => {
                        setFormSceneId(e.target.value);
                        // Reset box selection — old box may not belong to new scene.
                        setFormPluginBoxId("*");
                      }}
                    >
                      {sectionScenes.length === 0 && (
                        <option value="">
                          {i18n.t("Workflow.no_scenes", { defaultValue: "(no scenes)" })}
                        </option>
                      )}
                      {sectionScenes.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.title || s.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="workflow-tab__add-trigger-field">
                    <span>
                      {i18n.t("Workflow.plugin_box", { defaultValue: "Interactive box" })}
                    </span>
                    <select
                      value={formPluginBoxId}
                      onChange={(e) => setFormPluginBoxId(e.target.value)}
                    >
                      <option value="*">
                        {i18n.t("Workflow.any_box", { defaultValue: "Any (*)" })}
                      </option>
                      {filteredInteractiveBoxes.map((b) => (
                        <option key={b.boxId} value={b.boxId}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="workflow-tab__add-trigger-actions">
                    <button type="button" onClick={closeForm}>
                      {i18n.t("Workflow.cancel", { defaultValue: "Cancel" })}
                    </button>
                    <button
                      type="submit"
                      className="workflow-tab__toolbar-btn--primary"
                      disabled={!hasSection}
                    >
                      {i18n.t("Workflow.create", { defaultValue: "Create" })}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
          {substep !== "graph" && (
            <button type="button" onClick={() => setSubstep("graph")}>
              ← {i18n.t("Workflow.back_to_graph", { defaultValue: "Back to graph" })}
            </button>
          )}
        </div>
        <div className="workflow-tab__body">
          {substep === "graph" && (
            <div className="workflow-tab__graph workflow-tab__graph--modal">
              {/* Phase C item 7: the modal Workflow tab body is the element-as-node
                  graph (CanvasWorkflowPanel in modal mode). Same graph the docked
                  right-rail renders — same Redux source, same buildElementGraph,
                  same selectInteractiveItem dispatch — just full-width inside the
                  modal with the panel's docked chrome (header, border, shadow)
                  hidden. ShowCodePanel still slides in over it when the toolbar's
                  Show Code button is clicked. */}
              <CanvasWorkflowPanel mode="modal" />
              <ShowCodePanel
                show={showCode}
                onClose={() => setShowCode(false)}
                workflow={{ variables, triggers, scoringMode }}
                sectionId={sectionId}
              />
              {/* === Pedagogy Reviewer === */}
              <ReviewDrawer
                show={showReview}
                loading={reviewLoading}
                error={reviewError}
                reviews={reviewItems}
                onClose={() => setShowReview(false)}
                onRetry={handleRunReview}
                sectionId={sectionId}
              />
              {/* === /Pedagogy Reviewer === */}
            </div>
          )}
          {substep === "presets" && <PresetsSubstep onBack={() => setSubstep("graph")} />}
          {substep === "wizard" && <WizardSubstep onBack={() => setSubstep("graph")} />}
        </div>
      </div>
    </WorkflowEditContext.Provider>
  );
}

// Build the list of scenes in the active section and the list of
// InteractivePlugin boxes within those scenes. Done at mapStateToProps time so
// the component doesn't have to reach back into `state` itself. Cheap walk:
// section sizes are small (typically <20 scenes, <50 boxes).
function selectAddTriggerFormChoices(state, sectionId) {
  const present = (state && state.undoGroup && state.undoGroup.present) || {};
  const navItemsById = present.navItemsById || {};
  const pluginToolbarsById = present.pluginToolbarsById || {};
  if (sectionId === null || sectionId === undefined) {
    return { scenes: [], interactiveBoxes: [] };
  }
  const scenes = [];
  const interactiveBoxes = [];
  Object.keys(navItemsById).forEach((id) => {
    const item = navItemsById[id];
    // Use `==` to match the selector's existing scene/section parent contract
    // (`0` as section root, numeric vs. string ids).
    // eslint-disable-next-line eqeqeq
    if (!item || item.parent != sectionId || item.type === "section") return;
    scenes.push({ id: item.id, title: item.title || item.name || String(item.id) });
    if (!Array.isArray(item.boxes)) return;
    item.boxes.forEach((boxId) => {
      const tb = pluginToolbarsById[boxId];
      if (!tb || tb.pluginId !== "InteractivePlugin") return;
      interactiveBoxes.push({
        boxId,
        sceneId: item.id,
        label: `${item.title || item.id} · ${boxId}`,
      });
    });
  });
  return { scenes, interactiveBoxes };
}

const mapStateToProps = (state) => {
  const base = selectSectionWorkflow(state);
  const { scenes, interactiveBoxes } = selectAddTriggerFormChoices(state, base.sectionId);
  const present = (state && state.undoGroup && state.undoGroup.present) || {};
  return {
    ...base,
    scenes,
    interactiveBoxes,
    unreachableNodeIds: base.unreachableNodeIds,
    currentSceneId: present.navItemSelected || null,
  };
};

export default connect(mapStateToProps)(WorkflowTab);
